import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createSalesTransactionArtifact,
  createPaymentReceiptArtifact,
  createFinanceIntakeRequest,
  ArtifactState,
  SystemOwner,
  canEditArtifact,
  canSubmitArtifact,
  isArtifactImmutable
} from '../lib/artifacts/types.js';
import {
  addArtifact,
  updateArtifact,
  getArtifactById,
  transitionArtifactState,
  deleteArtifact
} from '../lib/artifacts/storage.js';

export default function TransactionEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';

  const [artifact, setArtifact] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [narrative, setNarrative] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [channelAttribution, setChannelAttribution] = useState('');
  const [attachedPayments, setAttachedPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [metadata, setMetadata] = useState({});

  useEffect(() => {
    if (!isNew) {
      const loaded = getArtifactById(id);
      if (loaded) {
        setArtifact(loaded);
        setLineItems(loaded.data.lineItems || []);
        setNarrative(loaded.data.narrative || '');
        setCustomerRef(loaded.data.customerReference || '');
        setChannelAttribution(loaded.data.channelAttribution || '');
        setMetadata(loaded.data.metadata || {});
        
        // Load attached payment artifacts if referenced
        if (loaded.data.metadata?.paymentIds) {
          const payments = loaded.data.metadata.paymentIds.map(id => getArtifactById(id)).filter(Boolean);
          setAttachedPayments(payments);
        }
      } else {
        navigate('/sales/transactions');
      }
    }
  }, [id, isNew, navigate]);

  const editable = isNew || (artifact && canEditArtifact(artifact, SystemOwner.SALES));
  const submittable = artifact && canSubmitArtifact(artifact, SystemOwner.SALES);
  const totalAmount = lineItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);

  function handleSaveDraft() {
    const data = {
      lineItems,
      narrative,
      customerReference: customerRef,
      channelAttribution,
      totalAmount,
      transactionDate: new Date().toISOString()
    };

    if (isNew) {
      const newArtifact = createSalesTransactionArtifact(data);
      addArtifact(newArtifact);
      navigate(`/sales/transactions/${newArtifact.id}`);
    } else {
      updateArtifact(artifact.id, data);
      const updated = getArtifactById(artifact.id);
      setArtifact(updated);
    }
  }

  function handleSubmitToFinance() {
    if (!submittable) return;

    // Transition artifact to submitted state
    const submitted = transitionArtifactState(artifact.id, ArtifactState.SUBMITTED);

    // Create Finance Intake Request
    const intakeRequest = createFinanceIntakeRequest(
      artifact.id,
      attachedPayments.map(p => p.id)
    );
    addArtifact(intakeRequest);

    alert(`Submitted to Finance!\n\nFinance Intake Request: ${intakeRequest.id}\nSales Transaction: ${submitted.id}`);
    navigate('/sales/transactions');
  }

  function handleDelete() {
    if (!artifact || artifact.state !== ArtifactState.DRAFT) return;
    if (confirm('Delete this draft transaction?')) {
      deleteArtifact(artifact.id);
      navigate('/sales/transactions');
    }
  }

  function addLineItem() {
    setLineItems([...lineItems, { 
      description: '', 
      quantity: 1, 
      price: 0 
    }]);
  }

  function updateLineItem(index, field, value) {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  }

  function removeLineItem(index) {
    setLineItems(lineItems.filter((_, i) => i !== index));
  }

  function attachPaymentReceipt(paymentData) {
    const receipt = createPaymentReceiptArtifact(paymentData);
    addArtifact(receipt);
    setAttachedPayments([...attachedPayments, receipt]);
    setShowPaymentModal(false);
  }

  return (
    <div className="min-h-screen surface p-6">
      {/* Header */}
      <div className="mb-8">
        <button 
          className="text-mist hover:text-ice mb-4"
          onClick={() => navigate('/sales/transactions')}
        >
          ← Back to Transactions
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-ice text-3xl font-bold mb-2">
              {isNew ? 'New Sales Transaction' : artifact?.id}
            </h1>
            {artifact && (
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${
                  artifact.state === ArtifactState.DRAFT ? 'text-yellow-400' :
                  artifact.state === ArtifactState.SUBMITTED ? 'text-blue-400' :
                  artifact.state === ArtifactState.APPROVED ? 'text-green-400' :
                  'text-mist'
                }`}>
                  {artifact.state.toUpperCase()}
                </span>
                <span className="text-mist/70 text-sm">
                  {editable ? '✎ Editable' : '🔒 Read-Only'}
                </span>
              </div>
            )}
          </div>

          <div className="text-right">
            <div className="text-mist/70 text-sm mb-1">Total Amount</div>
            <div className="text-ice text-3xl font-bold">
              ${totalAmount.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl space-y-6">
        {/* Customer & Channel */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-mist text-sm mb-2">Customer Reference</label>
            <input
              type="text"
              value={customerRef}
              onChange={(e) => setCustomerRef(e.target.value)}
              disabled={!editable}
              className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2 disabled:opacity-50"
              placeholder="Customer name or ID"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-2">Channel Attribution</label>
            <input
              type="text"
              value={channelAttribution}
              onChange={(e) => setChannelAttribution(e.target.value)}
              disabled={!editable}
              className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2 disabled:opacity-50"
              placeholder="e.g., eBay → PayPal"
            />
          </div>
        </div>

        {/* Narrative */}
        <div>
          <label className="block text-mist text-sm mb-2">Sales Narrative</label>
          <textarea
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            disabled={!editable}
            rows={3}
            className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2 disabled:opacity-50"
            placeholder="Description of the sale..."
          />
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-ice text-lg font-semibold">Line Items</h3>
            {editable && (
              <button
                onClick={addLineItem}
                className="chip px-4 py-2 rounded-none"
              >
                + Add Item
              </button>
            )}
          </div>

          {lineItems.length === 0 ? (
            <div className="border border-edge rounded-hard bg-ink p-8 text-center text-mist">
              No line items. Click "Add Item" to begin.
            </div>
          ) : (
            <div className="space-y-2">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-2 items-center bg-ink border border-edge rounded-none p-3">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    disabled={!editable}
                    placeholder="Item description"
                    className="flex-1 bg-transparent text-ice border-none outline-none disabled:opacity-50"
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0)}
                    disabled={!editable}
                    className="w-20 bg-transparent text-ice text-right border-none outline-none disabled:opacity-50"
                    min="1"
                  />
                  <span className="text-mist">×</span>
                  <input
                    type="number"
                    value={item.price}
                    onChange={(e) => updateLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                    disabled={!editable}
                    className="w-24 bg-transparent text-ice text-right border-none outline-none disabled:opacity-50"
                    step="0.01"
                    min="0"
                  />
                  <span className="text-mist">=</span>
                  <div className="w-24 text-ice text-right font-semibold">
                    ${(item.quantity * item.price).toFixed(2)}
                  </div>
                  {editable && (
                    <button
                      onClick={() => removeLineItem(index)}
                      className="text-red-400 hover:text-red-300 px-2"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attached Payments */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-ice text-lg font-semibold">Attached Payment Receipts</h3>
            {editable && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="chip px-4 py-2 rounded-none"
              >
                + Attach Payment
              </button>
            )}
          </div>

          {attachedPayments.length === 0 ? (
            <div className="border border-edge rounded-hard bg-ink p-6 text-center text-mist/70 text-sm">
              No payment receipts attached
            </div>
          ) : (
            <div className="space-y-2">
              {attachedPayments.map(payment => (
                <div key={payment.id} className="bg-ink border border-edge rounded-none p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-ice font-medium">{payment.id}</div>
                        <span className="text-xs text-mist/70 bg-[#0e1d26] px-2 py-0.5 rounded">
                          {payment.owner.toUpperCase()} • Read-Only
                        </span>
                      </div>
                      <div className="text-mist/70 text-sm">{payment.data.payerEmail}</div>
                      <div className="text-mist/70 text-xs mt-1">
                        {new Date(payment.createdAt).toLocaleString()}
                      </div>
                      {payment.data.metadata?.note && (
                        <div className="text-mist text-sm mt-2 italic">"{payment.data.metadata.note}"</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-ice font-semibold text-lg">${payment.data.netAmount.toFixed(2)}</div>
                      <div className="text-mist/70 text-xs">Net Received</div>
                      <div className="text-mist/70 text-xs mt-1">
                        Gross: ${payment.data.grossAmount.toFixed(2)}
                      </div>
                      <div className="text-mist/70 text-xs">
                        Fees: -${payment.data.fees.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Payment Summary */}
              {attachedPayments.length > 1 && (
                <div className="border-t border-edge pt-3 mt-3">
                  <div className="flex justify-between text-sm">
                    <div className="text-mist">Total Gross:</div>
                    <div className="text-ice font-semibold">
                      ${attachedPayments.reduce((sum, p) => sum + p.data.grossAmount, 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <div className="text-mist">Total Fees:</div>
                    <div className="text-red-400">
                      -${attachedPayments.reduce((sum, p) => sum + p.data.fees, 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex justify-between text-sm mt-1 pt-1 border-t border-edge/50">
                    <div className="text-mist font-medium">Total Net Received:</div>
                    <div className="text-green-400 font-bold">
                      ${attachedPayments.reduce((sum, p) => sum + p.data.netAmount, 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Metadata Display (read-only view of additional context) */}
        {metadata && Object.keys(metadata).length > 0 && (
          <div>
            <h3 className="text-ice text-lg font-semibold mb-3">Transaction Metadata</h3>
            <div className="border border-edge rounded-hard bg-ink p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {metadata.totalGrossReceived && (
                  <div>
                    <div className="text-mist/70 mb-1">Total Gross Received</div>
                    <div className="text-ice font-semibold">${metadata.totalGrossReceived.toFixed(2)}</div>
                  </div>
                )}
                {metadata.totalNetReceived && (
                  <div>
                    <div className="text-mist/70 mb-1">Total Net Received</div>
                    <div className="text-green-400 font-semibold">${metadata.totalNetReceived.toFixed(2)}</div>
                  </div>
                )}
                {metadata.totalPayPalFees && (
                  <div>
                    <div className="text-mist/70 mb-1">PayPal Fees</div>
                    <div className="text-red-400">${metadata.totalPayPalFees.toFixed(2)}</div>
                  </div>
                )}
                {metadata.totalShippingCosts && (
                  <div>
                    <div className="text-mist/70 mb-1">Shipping Costs</div>
                    <div className="text-ice">${metadata.totalShippingCosts.toFixed(2)}</div>
                  </div>
                )}
                {metadata.damageRepairCost && (
                  <div>
                    <div className="text-mist/70 mb-1">Damage Repair</div>
                    <div className="text-ice">${metadata.damageRepairCost.toFixed(2)}</div>
                  </div>
                )}
                {metadata.insuranceRecovery && (
                  <div>
                    <div className="text-mist/70 mb-1">Insurance Recovery</div>
                    <div className="text-green-400">${metadata.insuranceRecovery.toFixed(2)}</div>
                  </div>
                )}
              </div>
              
              {(metadata.hadShippingIssues || metadata.hadInsuranceClaim) && (
                <div className="mt-3 pt-3 border-t border-edge text-xs space-y-1">
                  {metadata.hadShippingIssues && (
                    <div className="text-yellow-400">⚠️ Shipping complications occurred</div>
                  )}
                  {metadata.hadInsuranceClaim && (
                    <div className="text-blue-400">📋 Insurance claim filed</div>
                  )}
                  {metadata.upgradeOffered && (
                    <div className="text-green-400">✨ Customer upgrade provided</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-6 border-t border-edge">
          {editable && (
            <>
              <button
                onClick={handleSaveDraft}
                className="bg-brand text-ink px-6 py-3 rounded-none font-semibold"
              >
                {isNew ? 'Create Draft' : 'Update Draft'}
              </button>
              {!isNew && (
                <button
                  onClick={handleDelete}
                  className="border border-red-400 text-red-400 hover:bg-red-400/10 px-6 py-3 rounded-none"
                >
                  Delete Draft
                </button>
              )}
            </>
          )}

          {submittable && (
            <button
              onClick={handleSubmitToFinance}
              className="bg-green-600 text-white px-6 py-3 rounded-none font-semibold"
            >
              Submit to Finance →
            </button>
          )}

          {!editable && !isNew && (
            <div className="text-mist/70 py-3">
              This artifact is {artifact?.state} and cannot be edited.
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          onClose={() => setShowPaymentModal(false)}
          onAttach={attachPaymentReceipt}
        />
      )}
    </div>
  );
}

function PaymentModal({ onClose, onAttach }) {
  const [transactionId, setTransactionId] = useState('');
  const [grossAmount, setGrossAmount] = useState(0);
  const [fees, setFees] = useState(0);
  const [payerEmail, setPayerEmail] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    onAttach({
      transactionId,
      grossAmount: parseFloat(grossAmount),
      fees: parseFloat(fees),
      netAmount: parseFloat(grossAmount) - parseFloat(fees),
      payerEmail,
      timestamp: new Date().toISOString(),
      status: 'completed'
    });
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#0b171d] border border-edge rounded-hard p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-ice text-xl font-bold mb-4">Attach PayPal Receipt</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-mist text-sm mb-2">Transaction ID</label>
            <input
              type="text"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              required
              className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2"
              placeholder="PP-XXXXX"
            />
          </div>

          <div>
            <label className="block text-mist text-sm mb-2">Payer Email</label>
            <input
              type="email"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              required
              className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-mist text-sm mb-2">Gross Amount</label>
              <input
                type="number"
                value={grossAmount}
                onChange={(e) => setGrossAmount(e.target.value)}
                required
                step="0.01"
                min="0"
                className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2"
              />
            </div>
            <div>
              <label className="block text-mist text-sm mb-2">Fees</label>
              <input
                type="number"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                required
                step="0.01"
                min="0"
                className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2"
              />
            </div>
          </div>

          <div className="text-mist text-sm">
            Net Amount: <span className="text-ice font-semibold">${(parseFloat(grossAmount) - parseFloat(fees)).toFixed(2)}</span>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-brand text-ink px-4 py-2 rounded-none font-semibold"
            >
              Attach Receipt
            </button>
            <button
              type="button"
              onClick={onClose}
              className="chip px-4 py-2 rounded-none"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
